/**
 * Location labels from Excel vs master often differ by commas, dots, or extra spaces
 * ("Skin Laundry International, LTD" vs "Skin Laundry International LTD").
 */

const LEGAL_LOCATION_SUFFIX =
  /^(ltd|llc|inc|limited|corp|co|gmbh|plc|sa|sas|bv|nv|pty|lp|llp)$/i;

export const UNKNOWN_LOCATION_LABEL = 'Unknown location';

export function canonicalLocationKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function isMongoObjectIdString(value) {
  return /^[a-fA-F0-9]{24}$/.test(String(value || '').trim());
}

/**
 * Hide raw Mongo ids in chips and selected-value labels.
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export function hideRawObjectIdLabel(value, fallback = 'Unknown') {
  const text = String(value == null ? '' : value).trim();
  if (!text || isMongoObjectIdString(text)) return fallback;
  return text;
}

/**
 * Hide raw Mongo ids in location chips / table cells.
 * @param {unknown} value
 * @returns {string}
 */
export function displayLocationLabel(value) {
  return hideRawObjectIdLabel(value, UNKNOWN_LOCATION_LABEL);
}

/**
 * Split a location cell on commas without breaking legal-entity suffixes.
 * "Skin Laundry International, LTD" stays one name; "New York, London" stays two.
 * @param {unknown} value
 * @returns {string[]}
 */
export function splitLocationNames(value) {
  if (value == null || value === '') return [];

  const raw = Array.isArray(value) ? value : [value];
  const parts = raw.flatMap((item) =>
    String(item ?? '')
      .split(',')
      .map((part) => part.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
  );
  if (parts.length <= 1) return parts;

  const merged = [];
  parts.forEach((part) => {
    if (merged.length && LEGAL_LOCATION_SUFFIX.test(part.replace(/\./g, ''))) {
      merged[merged.length - 1] = `${merged[merged.length - 1]}, ${part}`;
      return;
    }
    merged.push(part);
  });
  return merged;
}

/**
 * Resolve location names against a canonical catalog when available.
 * Keeps a comma-containing label intact when it matches a known location.
 * @param {unknown} value
 * @param {Set<string>|null} [knownKeys]
 * @returns {string[]}
 */
export function resolveLocationNames(value, knownKeys = null) {
  if (value == null || value === '') return [];
  const raw = Array.isArray(value) ? value : [value];
  const known = knownKeys instanceof Set ? knownKeys : null;

  return raw.flatMap((item) => {
    const text = String(item ?? '').trim();
    if (!text) return [];
    const fullKey = canonicalLocationKey(text);
    if (known && fullKey && known.has(fullKey)) return [text];
    return splitLocationNames(text);
  });
}
